import { GridColDef } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../../../components/formatUtils/dateFormatUtils';
import {
  ApiFilters,
  mapApiFiltersToGridFilterItems,
  useTableApiQueryFilterState,
} from '../../../components/table/apiQueryFilterState.hook';
import { AppDataGrid } from '../../../components/table/AppDataGrid';
import { PaginationState } from '../../aufwand/api/aufwandApi';
import { KundeLink } from '../../kunde/components/KundeLink';
import { MitarbeiterLink } from '../../mitarbeiter/components/MitarbeiterLink';
import { Projekt } from '../api/projekt.types';
import { useFindAllProjektQuery } from '../api/projektApi';

const projektTableColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 3,
  },
  {
    field: 'kurzbeschreibung',
    headerName: 'Kurzbeschreibung',
    flex: 4,
    type: 'string',
  },
  {
    field: 'kundeId',
    headerName: 'Kunde',
    flex: 2,
    renderCell: params => <KundeLink id={params.value} />,
  },
  {
    field: 'projektLeiterId',
    headerName: 'Projektleiter',
    flex: 2,
    renderCell: params => params.value && <MitarbeiterLink id={params.value} />,
  },
  {
    field: 'rechnungszyklus',
    headerName: 'Rechnungszyklus',
    flex: 2,
    type: 'number',
  },
  {
    field: 'istPauschal',
    headerName: 'Pauschal',
    flex: 1,
    type: 'boolean',
  },
  {
    field: 'istPausiert',
    headerName: 'Pausiert',
    flex: 1,
    type: 'boolean',
  },
  {
    field: 'istArchiviert',
    headerName: 'Archiviert',
    flex: 1,
    type: 'boolean',
  },
  {
    field: 'creationDate',
    headerName: 'Angelegt am',
    flex: 1,
    type: 'date',
    valueFormatter: value => formatDate(value as string),
  },
];

interface ProjektTableProps {
  initialFilterItems?: ApiFilters;
}

interface SortState {
  [key: string]: 'asc' | 'desc';
}

export const ProjektTable: FC<ProjektTableProps> = ({ initialFilterItems }) => {
  const navigate = useNavigate();

  const defaultFilterItems = useMemo(
    () => ({
      istArchiviert: { value: false, operator: 'equal' },
      ...(initialFilterItems ?? {}),
    }),
    [initialFilterItems]
  );

  const columns = useMemo(() => {
    return projektTableColumns.map(ptc => ({ ...ptc }));
  }, []);

  const { gridApiRef, apiFilters, onFilterModelChange } =
    useTableApiQueryFilterState<Projekt>(defaultFilterItems);

  const initialFiltersMapped = useMemo(
    () => mapApiFiltersToGridFilterItems(defaultFilterItems),
    [defaultFilterItems]
  );

  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 0,
    pageSize: 25,
  });

  const [sortState, setSortState] = useState<SortState>();

  const { data, isFetching } = useFindAllProjektQuery({
    filter: apiFilters,
    pagination: paginationState,
    sort: sortState,
  });

  const projekte = data?.data ?? [];

  return (
    <AppDataGrid
      onRowClick={params => navigate(`/intern/projekte/${params.id}`)}
      apiRef={gridApiRef}
      checkboxSelection={false}
      loading={isFetching}
      columns={columns}
      rows={projekte}
      filterMode="server"
      initialState={{
        filter: {
          filterModel: {
            items: initialFiltersMapped,
          },
        },
        columns: {
          columnVisibilityModel: (initialFilterItems ? Object.keys(initialFilterItems) : []).reduce(
            (acc, key) => {
              acc[key] = false;
              return acc;
            },
            {} as Record<string, boolean>
          ),
        },
      }}
      onFilterModelChange={onFilterModelChange}
      pagination
      paginationMode="server"
      rowCount={data?.pagination.count ?? 0}
      paginationModel={paginationState}
      onPaginationModelChange={setPaginationState}
      pageSizeOptions={[5, 25, 50, 100, 200]}
      sortingMode="server"
      onSortModelChange={model =>
        setSortState(
          model.reduce((acc, cur) => {
            acc[cur.field] = cur.sort ? (_.upperCase(cur.sort) as 'asc' | 'desc') : 'asc';
            return acc;
          }, {} as SortState)
        )
      }
    />
  );
};
